import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Dialog,
  DialogContent,
  Button,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import { faBarChart } from "@fortawesome/free-solid-svg-icons";
import CoursePlanningLogViewer from "./inc/CoursePlanningLogViewer";
import { useAuth } from "../../../config/AuthContext";
import { Link } from "react-router-dom";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../../config/firebase";
import useExportXCSI from "../../../hooks/useExportXCSI";
import ModalDialogTitle from "../../common/ModalDialogTitle";

const CcpAdminCoursePlanning = () => {
  const { currentUser } = useAuth();
  const { exportXCSI, axiosLoading: exportAxiosLoading } = useExportXCSI();
  const [exporting, setExporting] = useState(false);
  const [exportSnackbarOpen, setExportSnackbarOpen] = useState(false);
  const [exportSnackbarMessage, setExportSnackbarMessage] = useState("");
  const [exportSnackbarSeverity, setExportSnackbarSeverity] =
    useState("success");
  // State for selected course and dialog
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // State for Course Planning hide date
  const [hideDate, setHideDate] = useState("");
  const [saving, setSaving] = useState(false);

  const handleCourseClick = (course) => {
    setSelectedCourse(course);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedCourse(null);
  };

  // Load the current hide date from Firestore on mount
  useEffect(() => {
    const fetchHideDate = async () => {
      try {
        const docRef = doc(db, "forms", "ix25Z19hd05vXVjPivyG");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setHideDate(docSnap.data().coursePlanning || "");
        }
      } catch (e) {
        // Optionally handle error
      }
    };
    fetchHideDate();
  }, []);

  // Save the date to Firestore on change
  const handleHideDateChange = async (e) => {
    const value = e.target.value;
    setHideDate(value);
    setSaving(true);
    try {
      const docRef = doc(db, "forms", "ix25Z19hd05vXVjPivyG");
      await updateDoc(docRef, { coursePlanning: value });
    } catch (e) {
      // Optionally handle error
    }
    setSaving(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportXCSI();
      setExportSnackbarMessage(
        `Export complete: ${result.rowsCount || 0} rows`,
      );
      setExportSnackbarSeverity("success");
      setExportSnackbarOpen(true);
    } catch (err) {
      console.error("Export failed:", err);
      setExportSnackbarMessage("Export failed. Check console for details.");
      setExportSnackbarSeverity("error");
      setExportSnackbarOpen(true);
    } finally {
      setExporting(false);
    }
  };

  const pageName = "Course Planning Administration";
  const icon = faBarChart;

  // If there is no logged-in user, show access denied message
  if (!currentUser) {
    return (
      <Box
        sx={{
          marginTop: "100px",
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 100px)",
        }}
      >
        {/* Access Denied Message */}
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 15px",
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1">
              You must be logged in to access this page.
            </Typography>
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 0,
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "none",
        margin: 0,
      }}
    >
      <CoursePlanningLogViewer
        compact={false}
        showSchoolForm={true}
        excludeUserColumn
        onCourseClick={handleCourseClick}
        hideDate={hideDate}
        saving={saving}
        onHideDateChange={handleHideDateChange}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={exporting || exportAxiosLoading}
        >
          {exporting ? <CircularProgress size={16} /> : "Export XCSI"}
        </Button>
      </Box>

      {/* Course Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <ModalDialogTitle onClose={handleDialogClose}>
          Course Details
        </ModalDialogTitle>
        <DialogContent dividers>
          {selectedCourse ? (
            <Box>
              {(() => {
                const priorityKeys = [
                  "changeAction",
                  "description",
                  "specificChange",
                ];
                const remainingKeys = Object.keys(selectedCourse).filter(
                  (k) => !priorityKeys.includes(k),
                );
                const orderedEntries = [
                  ...priorityKeys
                    .filter((k) => k in selectedCourse)
                    .map((k) => [k, selectedCourse[k]]),
                  ...remainingKeys.map((k) => [k, selectedCourse[k]]),
                ];
                return orderedEntries.map(([key, value]) => (
                  <Box key={key} sx={{ mb: 1 }}>
                    <Typography variant="subtitle2">{key}</Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {typeof value === "object" && value !== null
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                    </Typography>
                  </Box>
                ));
              })()}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={exportSnackbarOpen}
        autoHideDuration={6000}
        onClose={() => setExportSnackbarOpen(false)}
      >
        <Alert
          severity={exportSnackbarSeverity}
          onClose={() => setExportSnackbarOpen(false)}
        >
          {exportSnackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CcpAdminCoursePlanning;
